import * as React from 'react';
import { observer } from 'mobx-react';
import { Employee } from 'nomst/Stores/Decision/employee';
import RangePenetrationComparisonChart from 'components/Charts/rangePenetrationComparisonChart';
import RangePenetrationChart from 'components/Charts/rangePenetrationChart';
import PayText from 'components/Common/payText';
import { AmountCode } from 'typings/enums';
import { Range } from 'nomst/Stores/Decision/range';
import { RangeChartHelper, Bar } from 'common/rangeChartHelper';
import './promotionSummaryView.scss';
import { Configuration } from 'nomst/Stores/Configuration/configuration';

interface PromotionSummaryViewProps {
    employee: Employee;
    configuration: Configuration;
}

export class PromotionSummaryView extends React.Component<PromotionSummaryViewProps> {

    render(): React.ReactNode {
        return (
            <div className="promotion-summary-view">
                {this.renderRangeChart()}
                {this.renderNewPayAdditionChart()}
            </div>
        );
    }

    private renderRangeChart(): React.ReactNode {
        const { employee, configuration } = this.props;
        if (!configuration.payRangeConfiguration.showPayRange) {
            return null;
        }

        const { promotionRequest } = employee;
        const currentJobRangeBar: Nullable<Bar> = this.getJobRangeBar(employee.range);
        let newJobRangeBar: Nullable<Bar> = this.getJobRangeBar(promotionRequest.proposedPayRange);

        if (!employee.range || !currentJobRangeBar) {
            return (
                <div className="promotion-range-chart promotion-range-chart--range-not-defined">
                    <h4 className="promotion-range-chart__heading">Range for Job</h4>
                    <strong className="promotion-range-chart__range-not-defined">Range is not defined</strong>
                </div>
            );
        }

        if (!promotionRequest.proposedJobCode) {
            // no new job selected, but current range exists
            return (
                <div className="promotion-range-chart promotion-range-chart--only-current-range">
                    <h4 className="promotion-range-chart__heading">Range for Job</h4>
                    <div className="promotion-range-chart__pay-range-numbers">
                        <span className="promotion-range-chart__pay-range-min">
                            {currentJobRangeBar.minText}
                        </span>
                        <span className="promotion-range-chart__pay-range-max">
                            {currentJobRangeBar.maxText}
                        </span>
                    </div>
                    <RangePenetrationChart
                        max={currentJobRangeBar.max}
                        min={currentJobRangeBar.min}
                        pay={employee.newPay}
                    />
                </div>
            );
        }

        let isNewRangeDisabled = false;
        if (!newJobRangeBar?.min && !newJobRangeBar?.max) {
            // new job range is not defined or is hidden from user
            newJobRangeBar = currentJobRangeBar;
            isNewRangeDisabled = true;
        }

        return (
            <div className="promotion-range-chart promotion-range-chart--both-ranges">
                <h4 className="promotion-range-chart__heading">Range for Job</h4>
                <RangePenetrationComparisonChart
                    currentJobRangeBar={currentJobRangeBar}
                    newJobRangeBar={newJobRangeBar}
                    pay={employee.newPay}
                    isNewRangeDisabled={isNewRangeDisabled}
                />
                {isNewRangeDisabled && (
                    <p className="promotion-range-chart__warning-text">
                        Range cannot be displayed until approved by your HR team
                    </p>
                )}
            </div>
        );
    }

    private renderNewPayAdditionChart(): React.ReactNode {
        const { employee } = this.props;
        return (
            <div className="comp-summary-display">
                <div className="comp-summary-display__row comp-summary-display__row--current-pay">
                    <div className="comp-summary-display__title">Current Pay</div>
                    <div className="comp-summary-display__value">
                        <PayText
                            payText={this.formatValue(employee.convertedCurrentPay)}
                            annualPayText={this.formatValue(employee.convertedAnnualizedCurrentPay)}
                            isHourly={employee.isHourly}
                            payTypeClassName="current-pay"
                        />
                    </div>
                </div>
                <div className="comp-summary-display__row comp-summary-display__row--increase">
                    <div className="comp-summary-display__title">Increase</div>
                    <div className="comp-summary-display__value">
                        <div data-mf-replace-inner="***">
                            {this.formatValue(employee.meritProratedIncrease, employee.isHourly)}
                        </div>
                    </div>
                </div>
                <div className="comp-summary-display__row comp-summary-display__row--promotion">
                    <div className="comp-summary-display__title">Promotion</div>
                    <div className="comp-summary-display__value">
                        {this.renderPromotionIncrease()}
                    </div>
                </div>
                <div className="comp-summary-display__row comp-summary-display__row--new-pay">
                    <div className="comp-summary-display__title">New Pay</div>
                    <div className="comp-summary-display__value">
                        <PayText
                            payText={this.formatValue(employee.newPay)}
                            annualPayText={this.formatValue(employee.annualizedNewPay)}
                            isHourly={employee.isHourly}
                            payTypeClassName="new-pay"
                        />
                    </div>
                </div>
            </div>
        );
    }

    private getJobRangeBar(range: Nullable<Range>): Nullable<Bar> {
        if (!range) {
            return null;
        }

        const { employee } = this.props;
        const bar = RangeChartHelper.getJobRangeBar(range);
        if (bar) {
            bar.minText = this.formatValue(range.minRange, employee.isHourly);
            bar.maxText = this.formatValue(range.maxRange, employee.isHourly);
        }
        return bar;
    }

    private formatValue(value: number, showPerHour: boolean = false): string {
        const { configuration, employee } = this.props;
        const currency = employee.getAmount(AmountCode.Promotion)?.amountIsoCurrencyCode || null;
        const formattedValue =  configuration.format.formatCurrencyValue(currency, value, employee.isHourly);
        return showPerHour ? `${formattedValue}/hour` : formattedValue.toString();
    }

    private renderPromotionIncrease(): React.ReactNode {
        const { employee } = this.props;
        const amount = employee.getAmount(AmountCode.Promotion);
        if (amount && amount.value > 0) {
            return (
                <div data-mf-replace-inner="***" className="comp-summary-display__converted-pay">
                    {this.formatValue(employee.convertedPromotionPay, employee.isHourly)}
                </div>
            );
        }

        return (
            <div className="comp-summary-display__not-provided">Not provided</div>
        );
    }
}

export default observer(PromotionSummaryView);